import {
  setDefaultEnvironment,
  setDefaultModuleVersion,
} from '@3ddv/dvm-internal';
import { DvmModule, MapViewerService } from '@3ddv/ngx-dvm-internal';
import { LoaderComponent } from '@3ddv/software-division-components/dvm/loader';
import {
  CONFIGURATION_SERVICE,
  DVM_FORMATTER,
  DVM_SERVICE,
  DvmPopoverPositionerService,
  POPOVER_MANAGER,
  POPOVER_POSITIONER,
  THUMBNAIL_PROVIDER,
} from '@3ddv/software-division-components/dvm/popover';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { DialogComponent } from '@3ddv/software-division-components/generic/dialog';
import { provideSdc } from '@3ddv/software-division-components/shared';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import { CookiePolicyComponent } from '@core/modals/legal-text/components/cookie-policy.component';
import { LegalNoticeComponent } from '@core/modals/legal-text/components/legal-notice.component';
import { PrivacyPolicyComponent } from '@core/modals/legal-text/components/privacy-policy.component';
import { LegalTextModalComponent } from '@core/modals/legal-text/legal-text.component';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { BG_MAP_VIEWER_TOKEN } from '@shared/ports/tokens/bg-map-viewer.token';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NotFoundComponent } from './core/components/not-found/not-found.component';
import { NumbersInputDirective } from './core/directives/numbers-input.directive';
import { DemoInterceptor } from './core/interceptors/demo.interceptor';
import { EnvironmentInterceptor } from './core/interceptors/environment.interceptor';
import { GroupLinkInterceptor } from './core/interceptors/group-link.interceptor';
import { FooterComponent } from './core/layout/footer/footer.component';
import { HeaderComponent } from './core/layout/header/header.component';
import { LayoutComponent } from './core/layout/layout.component';
import { PaginationModule } from './core/layout/pagination/pagination.module';
import { LoginComponent } from './core/login/login.component';
import { ForbiddenEventModalComponent } from './core/modals/forbidden-event-modal/forbidden-event-modal.component';
import { GaModalComponent } from './core/modals/ga-modal/ga-modal.component';
import { ModalsComponent } from './core/modals/modals.component';
import { RestrictedSeatsModalComponent } from './core/modals/restricted-seats-modal/restricted-seats-modal.component';
import { SeatDefinitionsModalComponent } from './core/modals/seat-definitions-modal/seat-definitions-modal.component';
import { TutorialWelcomeModalComponent } from './core/modals/tutorial-welcome-modal/tutorial-welcome-modal.component';
import { OktaIntegrationModule } from './core/okta-integration/okta-integration.module';
import { ConfigurationService } from './core/services/configuration.service';
import { DvmFormatterService } from './core/services/dvm-formatter.service';
import { DvmService } from './core/services/dvm.service';
import { GroupSalesPopoverManagerService } from './core/services/group-sales-popover-manager.service';
import { GroupSalesThumbnailProviderService } from './core/services/groupsales-thumbnail-provider.service';
import { TemplatePageTitleStrategyService } from './core/services/template-page-title-strategy.service';
import { CheckoutComponent } from './features/pages/checkout/checkout.component';
import { AddDigitalWalletModalComponent } from './features/pages/payment/components/add-digital-wallet-modal/add-digital-wallet-modal.component';
import { LegalTermsComponent } from './features/pages/payment/components/legal-terms/legal-terms.component';

setDefaultModuleVersion('map_viewer', 'stable');
setDefaultModuleVersion('3d_viewer', 'stable');
setDefaultEnvironment('core_pro');

/**
 * Initializes application with certain parameters fetched from the URL and backend.
 * It's intended to run before the application fully bootstraps.
 * @param http - The HTTP client used for making the request.
 * @returns A function that returns a promise, signaling the app initialization completion.
 */
function initializeApp(configurationService: ConfigurationService): () => void {
  return () => configurationService.init();
}

const appInitObject = {
  provide: APP_INITIALIZER,
  useFactory: initializeApp,
  deps: [ConfigurationService],
  multi: true,
};

const environmentInterceptorObject = {
  provide: HTTP_INTERCEPTORS,
  useClass: EnvironmentInterceptor,
  multi: true,
};

const demoInterceptorObject = {
  provide: HTTP_INTERCEPTORS,
  useClass: DemoInterceptor,
  multi: true,
};

const groupLinkInterceptorObject = {
  provide: HTTP_INTERCEPTORS,
  useClass: GroupLinkInterceptor,
  multi: true,
};

const titleStrategy = {
  provide: TitleStrategy,
  useClass: TemplatePageTitleStrategyService,
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ModalsComponent,
    SeatDefinitionsModalComponent,
    GaModalComponent,
    RestrictedSeatsModalComponent,
    NotFoundComponent,
    NumbersInputDirective,
    TutorialWelcomeModalComponent,
    ForbiddenEventModalComponent,
    LegalTextModalComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AddDigitalWalletModalComponent,
    ButtonComponent,
    DialogComponent,
    LoaderComponent,
    LayoutComponent,
    BrowserModule,
    AppRoutingModule,
    BrowserModule,
    CheckoutComponent,
    DvmModule,
    FooterComponent,
    FormsModule,
    HeaderComponent,
    LoadingBarHttpClientModule,
    LoadingBarModule,
    OktaIntegrationModule,
    PaginationModule,
    LegalNoticeComponent,
    PrivacyPolicyComponent,
    CookiePolicyComponent,
  ],
  providers: [
    titleStrategy,
    appInitObject,
    environmentInterceptorObject,
    demoInterceptorObject,
    groupLinkInterceptorObject,
    MapViewerService,
    { provide: BG_MAP_VIEWER_TOKEN, useClass: MapViewerService },
    DvmService,
    DvmFormatterService,
    { provide: DVM_SERVICE, useExisting: DvmService },
    { provide: DVM_FORMATTER, useExisting: DvmFormatterService },
    { provide: CONFIGURATION_SERVICE, useExisting: ConfigurationService },
    {
      provide: THUMBNAIL_PROVIDER,
      useExisting: GroupSalesThumbnailProviderService,
    },
    GroupSalesPopoverManagerService,
    { provide: POPOVER_MANAGER, useExisting: GroupSalesPopoverManagerService },
    { provide: POPOVER_POSITIONER, useClass: DvmPopoverPositionerService },
    provideHttpClient(withInterceptorsFromDi()),
    provideSdc(),
    LegalTermsComponent,
  ],
})
export class AppModule {}
